import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { PasswordInput } from '../components/common';

// ════════════════════════════════════════════════════════════════
// ForgotPasswordPage
// ════════════════════════════════════════════════════════════════
export function ForgotPasswordPage() {
  const [sent, setSent]     = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  async function onSubmit({ email }) {
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
    } catch {
      // Afișăm succes oricum — nu dezvăluim dacă emailul există
      setSent(true);
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.title}>Email trimis</h2>
          <p style={styles.text}>
            Dacă adresa există în sistem, vei primi un email cu instrucțiuni
            de resetare a parolei în câteva minute.
          </p>
          <p style={styles.textSmall}>
            Verifică și folderul Spam dacă nu primești emailul.
          </p>
          <Link to="/login" style={styles.btn}>Înapoi la login</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Resetare parolă</h2>
        <p style={styles.text}>
          Introdu adresa de email și îți trimitem un link de resetare.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              autoComplete="email"
              style={styles.input}
              {...register('email', {
                required: 'Email obligatoriu',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalid' }
              })}
            />
            {errors.email && <span style={styles.error}>{errors.email.message}</span>}
          </div>

          <button type="submit" disabled={loading} style={styles.btnPrimary}>
            {loading ? 'Se trimite...' : 'Trimite link resetare'}
          </button>
          <Link to="/login" style={styles.backLink}>Înapoi la login</Link>
        </form>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ResetPasswordPage
// ════════════════════════════════════════════════════════════════
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token');

  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Link invalid</h2>
          <p style={styles.text}>Link-ul de resetare este invalid sau a expirat.</p>
          <Link to="/forgot-password" style={styles.btnPrimary}>Solicită un link nou</Link>
        </div>
      </div>
    );
  }

  async function onSubmit({ password }) {
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, password });
      toast.success('Parolă schimbată cu succes!');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link expirat. Solicită un link nou.');
    } finally {
      setLoading(false);
    }
  }

  const passwordValue = watch('password', '');

  // Cerințe parolă
  const requirements = [
    { label: 'Minim 8 caractere',    met: passwordValue.length >= 8 },
    { label: 'O literă mare',        met: /[A-Z]/.test(passwordValue) },
    { label: 'O literă mică',        met: /[a-z]/.test(passwordValue) },
    { label: 'O cifră',              met: /\d/.test(passwordValue) },
    { label: 'Un caracter special',  met: /[@$!%*?&]/.test(passwordValue) },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Parolă nouă</h2>

        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Parolă nouă</label>
            <PasswordInput
              autoComplete="new-password"
              style={styles.input}
              {...register('password', {
                required: 'Parola este obligatorie',
                minLength: { value: 8, message: 'Minim 8 caractere' },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                  message: 'Parola nu îndeplinește cerințele'
                }
              })}
            />
          </div>

          {/* Indicator cerințe parolă */}
          <div style={styles.requirements}>
            {requirements.map(req => (
              <div key={req.label} style={styles.reqRow}>
                <span style={{ color: req.met ? '#3b6d11' : '#aaa', fontSize: '13px' }}>
                  {req.met ? '✓' : '○'} {req.label}
                </span>
              </div>
            ))}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirmă parola</label>
            <PasswordInput
              autoComplete="new-password"
              style={styles.input}
              {...register('confirmPassword', {
                required: 'Confirmarea parolei este obligatorie',
                validate: v => v === passwordValue || 'Parolele nu coincid'
              })}
            />
            {errors.confirmPassword && (
              <span style={styles.error}>{errors.confirmPassword.message}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || requirements.some(r => !r.met)}
            style={styles.btnPrimary}
          >
            {loading ? 'Se salvează...' : 'Salvează parola nouă'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', backgroundColor: '#f5f5f5', padding: '16px',
  },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '40px',
    width: '100%', maxWidth: '400px', boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
  },
  title:       { margin: '0 0 16px', fontSize: '22px', fontWeight: '700', color: '#1a1a2e' },
  text:        { margin: '0 0 24px', color: '#555', fontSize: '15px', lineHeight: '1.6' },
  textSmall:   { margin: '-16px 0 24px', color: '#888', fontSize: '13px' },
  form:        { display: 'flex', flexDirection: 'column', gap: '16px' },
  field:       { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:       { fontSize: '14px', fontWeight: '500', color: '#333' },
  input: {
    padding: '10px 14px', fontSize: '16px',
    border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none',
  },
  error:       { fontSize: '12px', color: '#e24b4a' },
  requirements: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '8px' },
  reqRow:      { display: 'flex', alignItems: 'center', gap: '8px' },
  btnPrimary: {
    display: 'block', textAlign: 'center', padding: '12px',
    backgroundColor: '#2d2d7e', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '16px', fontWeight: '600',
    cursor: 'pointer', textDecoration: 'none',
  },
  btn: {
    display: 'block', textAlign: 'center', padding: '12px',
    backgroundColor: '#2d2d7e', color: '#fff',
    borderRadius: '8px', textDecoration: 'none', fontWeight: '600',
  },
  backLink:    { textAlign: 'center', fontSize: '14px', color: '#666', textDecoration: 'none' },
  successIcon: { fontSize: '48px', textAlign: 'center', color: '#3b6d11', marginBottom: '16px' },
};

export default ForgotPasswordPage;
